import { useState, useMemo } from 'react';

/**
 * DataSourceSelector - Toggle between data sources in simulators
 *
 * Phase 8.2: Let users switch data sources to compare results
 * Use Case: "How do results differ if I use NIQ data vs. internal sales data vs. survey data?"
 */

export type DataSourceId = 'niq' | 'internal' | 'survey' | 'blended';

export interface DataSource {
  id: DataSourceId;
  name: string;
  nameEs: string;
  icon: string;
  lastUpdate: string; // ISO date string
  description: string;
  descriptionEs: string;
  coverage?: string;
  metrics?: string[];
}

interface DataSourceSelectorProps {
  selectedSource: DataSourceId;
  onSourceChange: (sourceId: DataSourceId) => void;
  language?: 'en' | 'es';
  /** Number of days after which data is considered stale */
  staleDays?: number;
  /** Custom data sources (overrides defaults) */
  sources?: DataSource[];
  className?: string;
}

// Default data sources
const DEFAULT_DATA_SOURCES: DataSource[] = [
  {
    id: 'niq',
    name: 'NIQ / Nielsen',
    nameEs: 'NIQ / Nielsen',
    icon: '📊',
    lastUpdate: '2026-01-30',
    description: 'Syndicated retail audit data',
    descriptionEs: 'Datos de auditoria retail sindicados',
    coverage: '85%',
    metrics: ['Volume', 'Value', 'Distribution', 'Price'],
  },
  {
    id: 'internal',
    name: 'Internal Sales',
    nameEs: 'Ventas Internas',
    icon: '📈',
    lastUpdate: '2026-02-05',
    description: 'Real-time internal sales data',
    descriptionEs: 'Datos de ventas internas en tiempo real',
    coverage: '100%',
    metrics: ['SKU Sales', 'Channel Mix', 'Promotions'],
  },
  {
    id: 'survey',
    name: 'Survey Data',
    nameEs: 'Datos Encuesta',
    icon: '📋',
    lastUpdate: '2025-11-20',
    description: 'Consumer panel survey results',
    descriptionEs: 'Resultados de panel de consumidores',
    coverage: 'n=1,500',
    metrics: ['Preferences', 'Elasticity', 'Willingness to Pay'],
  },
  {
    id: 'blended',
    name: 'Blended',
    nameEs: 'Combinado',
    icon: '🔗',
    lastUpdate: '2026-02-05',
    description: 'Weighted combination of all sources',
    descriptionEs: 'Combinacion ponderada de todas las fuentes',
    coverage: 'Multi-source',
    metrics: ['Optimized accuracy'],
  },
];

export function DataSourceSelector({
  selectedSource,
  onSourceChange,
  language = 'en',
  staleDays = 30,
  sources,
  className = '',
}: DataSourceSelectorProps) {
  const t = language === 'es';
  const dataSources = sources || DEFAULT_DATA_SOURCES;

  // Calculate staleness for each source
  const sourcesWithStaleness = useMemo(() => {
    const now = new Date();
    return dataSources.map((source) => {
      const lastUpdateDate = new Date(source.lastUpdate);
      const daysDiff = Math.floor((now.getTime() - lastUpdateDate.getTime()) / (1000 * 60 * 60 * 24));
      return {
        ...source,
        daysOld: daysDiff,
        isStale: daysDiff > staleDays,
        isVeryStale: daysDiff > staleDays * 2,
      };
    });
  }, [dataSources, staleDays]);

  const selectedSourceData = sourcesWithStaleness.find((s) => s.id === selectedSource);

  // Format date based on language
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'es' ? 'es-MX' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Get freshness indicator color
  const getFreshnessColor = (daysOld: number, isStale: boolean, isVeryStale: boolean) => {
    if (isVeryStale) return 'text-danger-400';
    if (isStale) return 'text-warning-400';
    if (daysOld <= 7) return 'text-success-400';
    return 'text-cyan-400';
  };

  return (
    <div className={className}>
      {/* Source Tabs */}
      <div className="flex flex-wrap gap-2 mb-3">
        {sourcesWithStaleness.map((source) => (
          <button
            key={source.id}
            onClick={() => onSourceChange(source.id)}
            className={`
              relative flex items-center gap-2 px-4 py-2.5 rounded-lg border-2 transition-all
              ${selectedSource === source.id
                ? 'border-primary-500 bg-primary-500/10'
                : 'border-surface-600 bg-surface-700/50 hover:border-surface-500 hover:bg-surface-600/50'
              }
            `}
          >
            <span className="text-lg">{source.icon}</span>
            <div className="text-left">
              <div className="font-medium text-white text-sm">
                {t ? source.nameEs : source.name}
              </div>
              <div className={`text-xs ${getFreshnessColor(source.daysOld, source.isStale, source.isVeryStale)}`}>
                {formatDate(source.lastUpdate)}
              </div>
            </div>
            {/* Staleness indicator badge */}
            {source.isStale && (
              <div
                className={`
                  absolute -top-1 -right-1 w-3 h-3 rounded-full
                  ${source.isVeryStale ? 'bg-danger-500' : 'bg-warning-500'}
                `}
                title={t ? 'Datos desactualizados' : 'Data is stale'}
              />
            )}
          </button>
        ))}
      </div>

      {/* Staleness Warning */}
      {selectedSourceData?.isStale && (
        <div
          className={`
            flex items-start gap-2 px-3 py-2 rounded-lg text-sm
            ${selectedSourceData.isVeryStale
              ? 'bg-danger-500/10 border border-danger-500/20'
              : 'bg-warning-500/10 border border-warning-500/20'
            }
          `}
        >
          <span className="text-lg">⚠️</span>
          <div>
            <span className={selectedSourceData.isVeryStale ? 'text-danger-400' : 'text-warning-400'}>
              {t ? (
                <>
                  Los datos de <strong>{selectedSourceData.nameEs}</strong> tienen{' '}
                  <strong>{selectedSourceData.daysOld}</strong> días de antigüedad.
                </>
              ) : (
                <>
                  <strong>{selectedSourceData.name}</strong> data is{' '}
                  <strong>{selectedSourceData.daysOld}</strong> days old.
                </>
              )}
            </span>
            <span className="text-text-secondary ml-1">
              {t
                ? 'Considera usar datos más recientes para mejores resultados.'
                : 'Consider using fresher data for better results.'}
            </span>
          </div>
        </div>
      )}

      {/* Source Details */}
      {selectedSourceData && (
        <div className="mt-3 flex items-center gap-4 text-xs text-text-tertiary">
          <span>
            {t ? 'Cobertura' : 'Coverage'}: <span className="text-text-secondary">{selectedSourceData.coverage}</span>
          </span>
          {selectedSourceData.metrics && (
            <span className="flex items-center gap-1">
              {t ? 'Métricas' : 'Metrics'}:
              <span className="text-text-secondary">
                {selectedSourceData.metrics.slice(0, 3).join(', ')}
                {selectedSourceData.metrics.length > 3 && ` +${selectedSourceData.metrics.length - 3}`}
              </span>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Compact version for inline use
 */
interface DataSourceBadgeProps {
  source: DataSource;
  language?: 'en' | 'es';
  onClick?: () => void;
}

export function DataSourceBadge({ source, language = 'en', onClick }: DataSourceBadgeProps) {
  const t = language === 'es';
  const now = new Date();
  const lastUpdateDate = new Date(source.lastUpdate);
  const daysOld = Math.floor((now.getTime() - lastUpdateDate.getTime()) / (1000 * 60 * 60 * 24));
  const isStale = daysOld > 30;

  return (
    <button
      onClick={onClick}
      className={`
        inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs transition-colors
        ${isStale
          ? 'bg-warning-500/10 text-warning-400 hover:bg-warning-500/20'
          : 'bg-surface-600 text-text-secondary hover:bg-surface-500'
        }
      `}
      title={`${t ? 'Fuente de datos' : 'Data source'}: ${t ? source.nameEs : source.name}`}
    >
      <span>{source.icon}</span>
      <span>{t ? source.nameEs : source.name}</span>
      {isStale && <span>⚠️</span>}
    </button>
  );
}

/**
 * Hook to get current data source configuration
 */
export function useDataSource(initialSource: DataSourceId = 'blended') {
  const [selectedSource, setSelectedSource] = useState<DataSourceId>(initialSource);

  const sourceConfig = useMemo(() => {
    return DEFAULT_DATA_SOURCES.find((s) => s.id === selectedSource) || DEFAULT_DATA_SOURCES[0];
  }, [selectedSource]);

  return {
    selectedSource,
    setSelectedSource,
    sourceConfig,
    isBlended: selectedSource === 'blended',
  };
}

export default DataSourceSelector;
